import { db, storage } from "../../../boot/firebase";
import { addDoc, collection, doc, getDoc, updateDoc } from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  uploadString,
  getDownloadURL,
} from "firebase/storage";
import Sample from "../../../classes/Sample";
import { LOCATION_INFO } from "../../../constants";

export const LISTEN_SAMPLES = (
  { dispatch },
  { next, error, unlisten } = {}
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: Sample,
    path: Sample.getPath(),
    stateKey: "samples",
    next,
    error,
    unlisten,
  });
};

export const GET_SAMPLE = async ({ commit }, sampleID) => {
  // Deliberately not routed through GET_DOC: that action rejects with the
  // bare string "Not found", and the public scan page matches on
  // `e?.message === "Sample not found"` to show its own error, which a
  // string rejection has no `message` for.
  const snapshot = await getDoc(
    doc(db, Sample.getPath(sampleID)).withConverter(Sample)
  );
  if (!snapshot.exists()) {
    throw new Error("Sample not found");
  }
  const sample = snapshot.data();
  commit("SET", { stateKey: "samples", stateID: sampleID, doc: sample });
  return sample;
};

export const CREATE_SAMPLE = async ({ state }, sampleData) => {
  const uid = state.user?.id;
  if (!uid) throw new Error("unauthenticated");

  const now = new Date();
  // Seed the initial location with the sample's home office (falling back to
  // the creating user's office) so a never-scanned sample shows as in-office
  // at home — and appears on the map, which only plots samples with lat/lng.
  const office = sampleData.homeLocation || state.user?.data?.location || null;
  const address = office ? LOCATION_INFO[office]?.address?.data || null : null;
  const data = {
    ...sampleData,
    status: "in-office",
    location: {
      lat: address?.lat || null,
      lng: address?.lng || null,
      timestamp: now,
      office,
      address,
      description: "",
    },
    created: now,
    createdBy: uid,
    modified: now,
    modifiedBy: uid,
  };

  const docRef = await addDoc(collection(db, Sample.getPath()), data);
  return docRef.id;
};

export const UPDATE_SAMPLE = async ({ state }, { sampleID, updates }) => {
  const uid = state.user?.id;
  if (!uid) throw new Error("unauthenticated");

  await updateDoc(doc(db, Sample.getPath(sampleID)), {
    ...updates,
    modified: new Date(),
    modifiedBy: uid,
  });
};

export const SCAN_SAMPLE = async (
  { state },
  { sampleID, action, location, borrowerName, borrowerContactID, discardReason }
) => {
  const uid = state.user?.id;
  if (!uid) throw new Error("unauthenticated");

  const now = new Date();
  // An explicit office (home location on Return, chosen destination on Drop
  // Off) wins over geofence detection from the coordinates.
  const office =
    location?.office ||
    Sample.getOfficeFromCoordinates(location?.lat, location?.lng);

  const updates = {
    modified: now,
    modifiedBy: uid,
    lastScannedBy: uid,
    location: {
      lat: location?.lat || null,
      lng: location?.lng || null,
      timestamp: now,
      office,
      address: location?.address || null,
      description: location?.description || "",
    },
  };

  switch (action) {
    case "drop-off":
      // Dropped at an office with no borrower = it's back in the office.
      updates.status =
        office && !borrowerName && !borrowerContactID
          ? "in-office"
          : "checked-out";
      updates.borrowerName = borrowerName || "";
      updates.borrowerContactID = borrowerContactID || null;
      break;
    case "return":
      updates.status = office ? "in-office" : "checked-out";
      updates.borrowerName = "";
      updates.borrowerContactID = null;
      break;
    case "discard":
      updates.status = "discarded";
      updates.discardReason = discardReason || "";
      updates.discardedBy = uid;
      updates.discardedAt = now;
      break;
    default:
      throw new Error("Invalid action");
  }

  await updateDoc(doc(db, Sample.getPath(sampleID)), updates);

  return { status: updates.status, office };
};

// Callable by unauthenticated customers scanning the QR label. The
// firestore rule for the public path allows only status+modified, so this
// write must stay limited to exactly those two fields.
export const MARK_READY_FOR_PICKUP = async (_, { sampleID }) => {
  await updateDoc(doc(db, Sample.getPath(sampleID)), {
    status: "ready-for-pickup",
    modified: new Date(),
  });
};

export const UPLOAD_SAMPLE_IMAGE = async ({ state }, { sampleID, image }) => {
  const uid = state.user?.id;
  if (!uid) throw new Error("unauthenticated");

  const id = image.id || `photo.${image.ext || "jpg"}`;
  const fileRef = storageRef(storage, `samples/${sampleID}/${id}`);

  if (image.src.startsWith("blob:")) {
    // If blob URL, get the blob first
    const blob = await (await fetch(image.src)).blob();
    await uploadBytes(fileRef, blob);
  } else {
    // If data URL, upload directly
    await uploadString(fileRef, image.src, "data_url");
  }
  const src = await getDownloadURL(fileRef);

  return {
    id,
    src,
    width: image.width || null,
    height: image.height || null,
  };
};
